import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Button, Col, Modal, Popconfirm, Row, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAbortJob, useCancelJob, useFilterJobRecords } from "../../../../common/api/queries/jobs";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import AntIcon from "../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import { ModalSizes, PageSizes, rowGutter, TableSizes } from "../../../../common/constants";
import { JobState } from "../../enums";
import { JobRecord, JobSettings } from "../../types";
import JobStateTag from "../JobStateTag";

interface Props {
  job: JobSettings;
}

const JobRecordsTableView = ({ job }: Props) => {
  const { t } = useTranslation();

  const [resultToShow, setResultToShow] = useState<Record<string, any>>();
  const [showOtherRecords, setShowOtherRecords] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const { data, isFetching } = useFilterJobRecords(
    showOtherRecords ? { id: job.id, object: { pageIndex: pageIndex, pageSize: PageSizes.TINY } } : undefined,
  );
  const cancelJob = useCancelJob();
  const abortJob = useAbortJob();

  const columns: ColumnsType<JobRecord> = [
    {
      key: "createdAt",
      title: t("common.createdAt"),
      width: 160,
      render: (_, record) => formatLocaleDateTime(record.createdAt),
    },
    {
      key: "state",
      title: t("job.enums.jobState._label"),
      width: 130,
      render: (_, record) => <JobStateTag state={record.state} style={{ marginRight: 0 }} />,
    },
    {
      key: "startedAt",
      title: t("job.attrs.record.startedAt"),
      width: 160,
      render: (_, record) => (record.startedAt ? formatLocaleDateTime(record.startedAt) : undefined),
    },
    {
      key: "finishedAt",
      title: t("job.attrs.record.finishedAt"),
      width: 160,
      render: (_, record) => (record.finishedAt ? formatLocaleDateTime(record.finishedAt) : undefined),
    },
    {
      key: "manuallyScheduledFor",
      title: t("job.attrs.record.manuallyScheduledFor"),
      width: 160,
      render: (_, record) =>
        record.manuallyScheduledFor ? formatLocaleDateTime(record.manuallyScheduledFor) : undefined,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 100,
      render: (_, record) => {
        switch (record.state) {
          case JobState.MANUALLY_SCHEDULED:
            return (
              <Popconfirm
                title={t("job.helpers.cancelJobConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() => cancelJob.mutate({ id1: job.id, id2: record.id })}
              >
                <ActionTextIcon icon="close" color="orange" text={t("common.cancel")} />
              </Popconfirm>
            );
          case JobState.IN_PROGRESS:
            return (
              <Popconfirm
                title={t("job.helpers.abortJobConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() => abortJob.mutate({ id1: job.id, id2: record.id })}
              >
                <ActionTextIcon icon="close" color="red" text={t("common.cancel")} />
              </Popconfirm>
            );
          default:
            return (
              <ActionTextIcon
                icon="eye"
                color="blue"
                text={t("common.show")}
                disabled={!isDefined(record.result)}
                onClick={() => setResultToShow(record.result)}
              />
            );
        }
      },
    },
  ];

  return (
    <>
      <p className="margin-top-small">
        <b>{t("job.sections.mostRecentRecords")}</b>
      </p>

      <Table<JobRecord>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: TableSizes.MEDIUM }}
        loading={isFetching}
        dataSource={data?.pageData ? data.pageData : job.mostRecentRecord ? [job.mostRecentRecord] : []}
        pagination={{
          ...paginationTableProps,
          hideOnSinglePage: true,
          placement: ["bottomEnd"],
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => setPageIndex(pageIndex - 1),
        }}
      />

      {job.mostRecentRecord && !showOtherRecords ? (
        <Row gutter={rowGutter}>
          <Col span={24} className="center-align">
            <div className="margin-top-small">
              <SecondaryButton size="small" onClick={() => setShowOtherRecords(true)}>
                {t("job.actions.showOtherRecords")}
              </SecondaryButton>
            </div>
          </Col>
        </Row>
      ) : undefined}

      <Modal
        open={isDefined(resultToShow)}
        title={t("job.titles.jobResult", { jobName: job.name })}
        width={ModalSizes.MEDIUM}
        centered
        onCancel={() => setResultToShow(undefined)}
        footer={
          <Button onClick={() => setResultToShow(undefined)} icon={<AntIcon type="close" />}>
            {t("common.close")}
          </Button>
        }
        mask={{ closable: false }}
      >
        {resultToShow && (
          <Row gutter={rowGutter} justify="center">
            <Col span={20}>
              <pre className="modal-code-block">{JSON.stringify(resultToShow, undefined, 2)}</pre>
            </Col>
          </Row>
        )}
      </Modal>
    </>
  );
};

export default JobRecordsTableView;
